import { OAUTH_CONSENT_WEB_PATH } from '@fos/shared/contracts/oauth';
import { Navigate, type RouteObject } from 'react-router';
import { AgentRunPage, AgentsPage } from '../features/agents';
import { ApprovalDetailPage, ApprovalsPage } from '../features/approvals';
import { ForgotPasswordPage } from '../features/auth/pages/ForgotPasswordPage';
import { InvitationPage } from '../features/auth/pages/InvitationPage';
import { LoginPage } from '../features/auth/pages/LoginPage';
import { ResetPasswordPage } from '../features/auth/pages/ResetPasswordPage';
import { BillingDraftPage, BillingListPage } from '../features/billing';
import { ClientProfilePage } from '../features/clients/pages/ClientProfilePage';
import { ClientSettingsPage } from '../features/clients/pages/ClientSettingsPage';
import { ClientsListPage } from '../features/clients/pages/ClientsListPage';
// Kompozycja Fali 1 (sloty profilu klienta, projektu, zakładka Usługi): rejestracja przy imporcie tras.
import '../features/composition';
import { TokensAndAgentsSettingsPage } from '../features/composition/settings-tokens';
import { InboxPage } from '../features/inbox';
import { KnowledgePage } from '../features/knowledge';
import { MailPage, ThreadPage } from '../features/mail';
import { AssetDetailPage, MaintenanceListPage } from '../features/maintenance';
import { MeetingDetailPage, MeetingsPage } from '../features/meetings';
import { NotFoundPage } from '../features/modules/pages/ModulePage';
import { IncidentPage } from '../features/monitoring';
import { OfferDetailPage, OffersListPage } from '../features/offers';
import { PortalLayout, portalRoutes } from '../features/portal';
import { ProjectDetailPage } from '../features/projects/pages/ProjectDetailPage';
import { ProjectsListPage } from '../features/projects/pages/ProjectsListPage';
import { AiMemoryPage } from '../features/settings/ai';
import { AuditLogPage } from '../features/settings/audit';
import { AutomationsSettingsPage } from '../features/settings/automations';
import { IntegrationsSettingsPage } from '../features/settings/integrations';
import { NotificationsSettingsPage } from '../features/settings/notifications';
import { ProfilePage } from '../features/settings/pages/ProfilePage';
import { SecurityPage } from '../features/settings/pages/SecurityPage';
import { SettingsLayout } from '../features/settings/pages/SettingsLayout';
import { MemberPermissionsPage } from '../features/settings/team/MemberPermissionsPage';
import { TeamSettingsPage } from '../features/settings/team/TeamSettingsPage';
import { ConnectorConsentPage } from '../features/settings/tokens';
import { TaskPage, TasksPage } from '../features/tasks';
import { TimePage } from '../features/time';
import { TodayPage } from '../features/today/pages/TodayPage';
import { PublicOnly, RequireAuth, RequirePortal, RequireTeam } from './guards';
import { AppShell } from './layout/AppShell';

/**
 * Trasy aplikacji. Moduły dopisują swoje trasy w `appRoutes` (CONVENTIONS §1: apps/web/src/app/routes.tsx).
 * Ścieżki po polsku, bez ogonków.
 */
export const appRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="/dzis" replace /> },
  { path: 'dzis', element: <TodayPage /> },
  { path: 'inbox/:id?', element: <InboxPage /> },
  { path: 'poczta', element: <MailPage /> },
  { path: 'poczta/:id', element: <ThreadPage /> },
  { path: 'akceptacje', element: <ApprovalsPage /> },
  { path: 'akceptacje/:id', element: <ApprovalDetailPage /> },
  { path: 'zadania', element: <TasksPage /> },
  { path: 'zadania/:id', element: <TaskPage /> },
  { path: 'klienci', element: <ClientsListPage /> },
  { path: 'klienci/:id', element: <ClientProfilePage /> },
  { path: 'klienci/:id/ustawienia', element: <ClientSettingsPage /> },
  { path: 'oferty', element: <OffersListPage /> },
  { path: 'oferty/:id', element: <OfferDetailPage /> },
  { path: 'spotkania', element: <MeetingsPage /> },
  { path: 'spotkania/:id', element: <MeetingDetailPage /> },
  { path: 'czas', element: <TimePage /> },
  { path: 'projekty', element: <ProjectsListPage /> },
  { path: 'projekty/:id', element: <ProjectDetailPage /> },
  { path: 'utrzymanie', element: <MaintenanceListPage /> },
  { path: 'utrzymanie/:id', element: <AssetDetailPage /> },
  { path: 'incydenty/:id', element: <IncidentPage /> },
  { path: 'rozliczenia', element: <BillingListPage /> },
  { path: 'rozliczenia/:id', element: <BillingDraftPage /> },
  { path: 'wiedza/:id?', element: <KnowledgePage /> },
  { path: 'agenci', element: <AgentsPage /> },
  { path: 'agenci/:id', element: <AgentRunPage /> },
  {
    path: 'ustawienia',
    element: <SettingsLayout />,
    children: [
      { index: true, element: <Navigate to="profil" replace /> },
      { path: 'profil', element: <ProfilePage /> },
      { path: 'bezpieczenstwo', element: <SecurityPage /> },
      { path: 'zespol', element: <TeamSettingsPage /> },
      { path: 'zespol/:membershipId', element: <MemberPermissionsPage /> },
      { path: 'integracje', element: <IntegrationsSettingsPage /> },
      { path: 'powiadomienia', element: <NotificationsSettingsPage /> },
      { path: 'automatyzacje', element: <AutomationsSettingsPage /> },
      { path: 'ai', element: <AiMemoryPage /> },
      { path: 'audyt', element: <AuditLogPage /> },
      { path: 'tokeny', element: <TokensAndAgentsSettingsPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

export const routes: RouteObject[] = [
  {
    element: <PublicOnly />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/nie-pamietam-hasla', element: <ForgotPasswordPage /> },
    ],
  },
  { path: '/reset-hasla', element: <ResetPasswordPage /> },
  { path: '/zaproszenie', element: <InvitationPage /> },
  {
    element: <RequireAuth />,
    children: [
      // portal klienta (17.10.2026): tylko osoba z rolą client; własny układ bez powłoki zespołu
      {
        element: <RequirePortal />,
        children: [{ path: '/portal', element: <PortalLayout />, children: portalRoutes }],
      },
      {
        // osoba klienta z każdej ścieżki zespołu trafia do /portal
        element: <RequireTeam />,
        children: [
          // zgoda konektora Claude (OAuth): bez powłoki aplikacji, po zalogowaniu (z 2FA) — docs/MCP_TOOLS.md §4.3
          { path: OAUTH_CONSENT_WEB_PATH, element: <ConnectorConsentPage /> },
          { path: '/', element: <AppShell />, children: appRoutes },
        ],
      },
    ],
  },
];
